import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';

const ImageUpload = ({ onImageUpload, currentImage, label = "Job/Gig Image", imagePosition = 50, onPositionChange }) => {
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  // Sync previewUrl when currentImage changes (e.g., edit modal loading job data)
  useEffect(() => {
    setPreviewUrl(currentImage || null);
  }, [currentImage]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (iOS may report HEIC/HEIF or empty string for camera photos)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension = /\.(jpe?g|png|webp|heic|heif)$/i.test(fileName);
    if (!allowedTypes.includes(file.type) && !(file.type === '' && hasValidExtension)) {
      toast.error('Only JPEG, PNG, WebP, and HEIC images are allowed');
      return;
    }

    // Validate file size (20MB raw - compression will bring it down)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 20MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Compress then upload
    setCompressing(true);
    let fileToUpload;
    try {
      fileToUpload = await compressImage(file, 'jobImage');
    } catch {
      fileToUpload = file;
    } finally {
      setCompressing(false);
    }

    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to upload images');
        setPreviewUrl(currentImage || null);
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await api.post('/api/upload-image', formData, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        timeout: UPLOAD_TIMEOUT,
      });

      if (response.data.success) {
        const imageUrl = response.data.image_url;
        onImageUpload(imageUrl);
        toast.success('Image uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setPreviewUrl(currentImage || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onImageUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-col gap-4">
        {previewUrl ? (
          <div className="relative rounded-lg overflow-hidden border-2 border-border">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-48 object-cover"
              style={{ objectPosition: `center ${imagePosition}%` }}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
              disabled={uploading}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || compressing}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer bg-transparent w-full"
          >
            <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, WebP, or HEIC (max 20MB)
            </p>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />

        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || compressing}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          {compressing ? 'Compressing...' : uploading ? 'Uploading...' : previewUrl ? 'Change Image' : 'Upload Image'}
        </Button>
      </div>

      {previewUrl && onPositionChange && (
        <div className="space-y-1">
          <Label className="text-sm">Adjust Image Position</Label>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Top</span>
            <input
              type="range"
              min="0"
              max="100"
              value={imagePosition}
              onChange={(e) => onPositionChange(parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
            />
            <span className="text-xs text-muted-foreground">Bottom</span>
          </div>
        </div>
      )}

      {!previewUrl && (
        <p className="text-xs text-muted-foreground mt-2">
          Adding an image makes your post more attractive and increases engagement on social media!
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
